import type { BookingBreakdown, Listing, PriceRule } from '@shared/types';

export function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getNightlyPriceCents(
  listing: Pick<Listing, 'basePrice' | 'priceRules'>,
  date: Date
): number {
  const dateStr = toDateStr(date);
  const rules = listing.priceRules || [];
  for (const rule of rules) {
    if (rule.startDate <= dateStr && dateStr <= rule.endDate) {
      if (rule.type === 'season' || rule.type === 'special_date') {
        return rule.price;
      }
      if (rule.type === 'weekend') {
        const day = date.getDay();
        if (day === 5 || day === 6) return rule.price;
      }
      if (rule.type === 'weekday') {
        const day = date.getDay();
        if (day >= 1 && day <= 4) return rule.price;
      }
    }
  }
  return listing.basePrice || 0;
}

export function calculateStayBreakdown(
  listing: Pick<Listing, 'basePrice' | 'priceRules'>,
  checkIn: Date,
  checkOut: Date
): BookingBreakdown {
  const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  const cleaningFee = 0;
  const serviceFeeRate = 0.1;
  const taxRate = 0.15;
  let subtotal = 0;
  const priceDetails: BookingBreakdown['priceDetails'] = [];

  for (let i = 0; i < nights; i++) {
    const date = new Date(checkIn);
    date.setDate(date.getDate() + i);
    const cents = getNightlyPriceCents(listing, date);
    subtotal += cents;
    priceDetails.push({
      date: toDateStr(date),
      price: cents,
      label: 'Night',
    });
  }

  const serviceFee = Math.round(subtotal * serviceFeeRate);
  const taxes = Math.round((subtotal + cleaningFee + serviceFee) * taxRate);
  const total = subtotal + cleaningFee + serviceFee + taxes;

  return {
    basePrice: listing.basePrice || 0,
    nights,
    subtotal,
    cleaningFee,
    serviceFee,
    taxes,
    total,
    currency: 'ZAR',
    priceDetails,
  };
}

export function getListingPriceDisplay(listing: Pick<Listing, 'basePrice' | 'priceRules'>): {
  primary: string;
  secondary?: string;
  fromCents: number;
  hasVariableRates: boolean;
} {
  const base = listing.basePrice || 0;
  const rules = listing.priceRules || [];
  if (!rules.length) {
    return {
      primary: (base / 100).toFixed(2),
      fromCents: base,
      hasVariableRates: false,
    };
  }

  const prices = [base, ...rules.map((r) => r.price)];
  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (min === max) {
    return {
      primary: (min / 100).toFixed(2),
      fromCents: min,
      hasVariableRates: false,
    };
  }

  return {
    primary: `${(min / 100).toFixed(2)} - ${(max / 100).toFixed(2)}`,
    secondary: 'Rates vary by date',
    fromCents: min,
    hasVariableRates: true,
  };
}

export function sortPriceRules(rules: PriceRule[]): PriceRule[] {
  return [...rules].sort((a, b) => a.startDate.localeCompare(b.startDate));
}

export function formatPriceRuleLabel(rule: PriceRule): string {
  return rule.label?.trim() || (rule.type === 'season' ? 'Seasonal rate' : 'Special rate');
}
